import React, { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';

import { getClientAccountFunds } from '../../../../redux/clients/clients.slice';
import { setPageTitle } from '../../../../redux/title/title.slice';

import AccountInfoCard from '../../../../components/ui/cards/accountInfoCard';
import AccountSummaryCard from '../../../../components/ui/cards/accountSummaryCard';
import Loader from '../../../../components/ui/loader';
import { getRelevantDate } from '../../../../helpers/maturityDateHelper';

function PracticeClientAccount() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { clientAccountFunds, loadingClientAccountFunds, clientAccounts, loadingClientAccounts } =
    useSelector((state) => state.clients);

  // 🔥 Log funds for debugging
  useEffect(() => {
    console.log('clientAccountFunds:', clientAccountFunds);
  }, [clientAccountFunds]);

  const selectedAccount = useMemo(() => {
    if (!clientAccounts || !id) return null;
    return clientAccounts.find((acc) => String(acc.id) === String(id));
  }, [clientAccounts, id]);

  // Fetch account funds
  useEffect(() => {
    if (!selectedAccount) return;

    dispatch(
      getClientAccountFunds({
        id,
        asof: getRelevantDate(selectedAccount.maturityDate, selectedAccount.data?.asof)
      })
    );
  }, [dispatch, id, selectedAccount]);

  // Set page title dynamically based on selected account
  useEffect(() => {
    if (Array.isArray(clientAccounts) && id) {
      const selectedAccount = clientAccounts.find((acc) => String(acc.id) === String(id));
      if (selectedAccount) {
        dispatch(setPageTitle(selectedAccount.productName ?? 'Account Summary'));
      }
    }
  }, [dispatch, clientAccounts, id]);

  const account = useMemo(() => {
    if (
      !clientAccountFunds ||
      clientAccountFunds.length === 0 ||
      !clientAccounts ||
      clientAccounts.length === 0
    ) {
      return null;
    }

    // 🔹 Find the selected account in clientAccounts
    const selectedAccount = clientAccounts.find((acc) => String(acc.id) === String(id));
    if (!selectedAccount) return null;

    // 🔹 Helper to format asset class nicely
    const formatAssetClass = (value) =>
      value
        ? value
            .toLowerCase()
            .replace(/[_-]/g, ' ') // handle cases like EQUITY_US
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : '';

    // 🔹 Calculate total closing for all FUND rows
    const totalClosing = clientAccountFunds
      .filter((row) => row.assetClass !== 'FEES') // optional if FEES exist
      .reduce((sum, row) => sum + Number(row.closePositionValueVal || 0), 0);

    return {
      accountNumber: selectedAccount.shortName ?? '',
      administrator: selectedAccount.source?.name ?? '',
      product: selectedAccount.productName ?? 'Unknown Product',

      closingDate: selectedAccount.data?.asof,

      model: {
        name: selectedAccount.productName ?? 'Unknown Product',
        closing: totalClosing
      },

      funds: clientAccountFunds.map((row) => {
        const instrument = row.instrument?.trim();

        return {
          name: instrument ? instrument : formatAssetClass(row.assetClass) || 'Unknown',
          closing: row.closePositionValueVal,
          type: row.assetClass,
          strategy: row.strategy
        };
      })
    };
  }, [clientAccountFunds, clientAccounts, id]);

  if (loadingClientAccountFunds || loadingClientAccounts) {
    return <Loader loading={true} message="Loading client account..." />;
  }

  if (!account) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography>No account data found</Typography>
      </Box>
    );
  }

  return (
    <Box p={3} display="flex" flexDirection="column" gap={3} width="100%">
      <AccountInfoCard account={account} />
      <AccountSummaryCard account={account} accountNumber={account.accountNumber} />
    </Box>
  );
}

export default PracticeClientAccount;
